import { useWatch } from 'react-hook-form';
import { FormField } from 'v5/design-system/Form';
import { InputGroup, InputGroupSlot } from 'v5/design-system/InputGroup';
import { cn } from 'v5/platform/dom/cn';
import { useFieldContext } from '../FieldContext';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type {
  FieldFrameSpec,
  FieldPartProps,
  PlaceholderSpec
} from './fieldComponent.types';
import { ghostRowClassName } from './listLayout';
import { NumberGroupInput } from './numberInput';

/** PERCENTAGE custom field (legacy `PERCENTAGE.jsx`): a 0–100 number with a `%` suffix. */
export function PercentageField({
  spec,
  path,
  hideLabel
}: FieldPartProps<FieldFrameSpec & PlaceholderSpec>) {
  const ctx = useFieldContext();
  const readOnly = ctx.mode === 'display';
  const percentage: number | null = useWatch({ name: path }) ?? null;

  if (readOnly && percentage === null) {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  return (
    <FormField
      name={path}
      render={({ field }) => (
        <FieldControlBase spec={spec} hideLabel={hideLabel}>
          <InputGroup
            size={ctx.size}
            variant={ctx.variant}
            className={cn(ctx.ghost && ghostRowClassName)}
            aria-readonly={readOnly}
          >
            <NumberGroupInput
              readOnly={readOnly}
              placeholder={spec.placeholder ?? 'Enter value'}
              // At rest the input is as wide as its value, so the % follows it.
              size={readOnly ? String(percentage).length : undefined}
              className={cn(readOnly && 'w-auto')}
              {...field}
            />
            <InputGroupSlot
              className={cn('text-fg-muted', readOnly && 'pl-0.5!')}
              aria-hidden="true"
            >
              %
            </InputGroupSlot>
          </InputGroup>
        </FieldControlBase>
      )}
    />
  );
}
